import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { PageLayout } from '@/components/layout/PageLayout'
import { Input } from '@/components/shadcn/input'
import {
  getGetApiCompaniesCurrentActionmodulesQueryKey,
  usePatchApiCompaniesCurrentActionmodulesOrder,
} from '@/api/generated/company-management-user/company-management-user'
import { Button } from '@/components/shadcn/button'
import { useGetApiCompaniesCurrentActionmodules } from '@/api/generated/company-management-user/company-management-user'
import { Can } from '@/components/permission/Can'
import { PermissionEntityType } from '@/api/model'
import { ActionCard } from './components/ActionCard'
import { EditDialog } from './components/EditDialog'
import type { ActionModule } from './types'
import { PageHeader } from '@/components/layout/PageHeader'
import { usePermissions } from '@/hooks/usePermissions'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { notify } from '@/lib/notify'
import { SortableGrid } from '@/components/common/sortable/SortableGrid'
import { DragHandle } from '@/components/common/sortable/DragHandle'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

const GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'

export default function ActionModulesPage() {
  const { t } = useTranslation(['action-modules', 'filters'])
  const { userHasPermission } = usePermissions()
  const canReorder = userHasPermission(PermissionEntityType.ActionModules, 'Edit')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  // Legacy metadata dialog, used only while the wizard is gated off (see feature flag below).
  const [editingModule, setEditingModule] = useState<ActionModule | null>(null)

  const { data: modulesData, isLoading, isError, refetch } = useGetApiCompaniesCurrentActionmodules()
  const queryClient = useQueryClient()

  // Persist the order; afterwards reload the list so the server-side `order`
  // values are the source of truth again.
  const { mutate: reorderModules } = usePatchApiCompaniesCurrentActionmodulesOrder({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentActionmodulesQueryKey() }),
      onError: (error) => notify.apiError(error, t('toasts.reorderError')),
    },
  })

  // The wizard is still being finished, so it's rolled out per-user via this flag:
  // - off → editing a module opens the legacy metadata dialog (below).
  // - on  → editing navigates to the full wizard editor page, and a create button appears.
  const wizardEnabled = useFeatureFlag('ActionModuleEditor')

  const modules: ActionModule[] = useMemo(() => {
    const raw = (modulesData as any)?.data ?? modulesData
    const items: any[] = Array.isArray(raw) ? raw : raw?.items ?? []
    return items.filter(m => !m.isDeleted).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [modulesData])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return modules
    return modules.filter(m => (m.name ?? '').toLowerCase().includes(q) || (m.description ?? '').toLowerCase().includes(q))
  }, [modules, search])

  // The pencil either opens the full wizard (flag on) or the legacy metadata dialog (flag off).
  // CMU scope: no companyId in the URL — the wizard uses the `companies/current` endpoints.
  function openEditor(module: ActionModule) {
    if (module.id == null) return
    if (wizardEnabled) navigate(`/action-modules/edit/${module.id}`)
    else setEditingModule(module)
  }

  return (
    <PageLayout>
      <PageHeader title={t('header.title')}>
        {wizardEnabled && (
          <Can entityType={PermissionEntityType.ActionModules} permission="Edit">
            <Button onClick={() => navigate('/action-modules/edit/new')}>
              <Plus className="size-4" />
              {t('header.create')}
            </Button>
          </Can>
        )}
      </PageHeader>
      <div className="relative w-64">
        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input
          placeholder={t('filters:searchbar.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-8 h-9"
        />
      </div>
      <LoaderGeneral isLoading={isLoading} isError={isError} isEmpty={filtered.length === 0}>
        {canReorder && !search.trim() 
          ? (// D&D only with edit permission and no active search (a filtered subset
             // could not be reordered meaningfully).
            <SortableGrid
              items={filtered.filter((m): m is ActionModule & { id: number } => m.id != null)}
              onReorder={(ids) => reorderModules({ data: ids })}
              className={GRID_CLASS}
              renderItem={(module, handle) => (
                <ActionCard module={module} onEdit={openEditor} dragHandle={<DragHandle {...handle} />} />
              )}
            />) 
          : (<div className={GRID_CLASS}>
              {filtered.map(module => (<ActionCard key={module.id} module={module} onEdit={openEditor} />))}
            </div>)
        }
      </LoaderGeneral>

      {editingModule && (
        <EditDialog
          module={editingModule}
          onClose={() => setEditingModule(null)}
          onSaved={refetch}
        />
      )}
    </PageLayout>
  )
}
